import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { DatePicker } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SortOrder } from 'src/api/common';
import {
  ChallengeEngagementPostMetrics,
  engagementsApi,
  EngagementSortBy,
  NotEngagedMemberMetrics,
  PostMemberEngagementMetrics,
} from 'src/api/engagements';
import styles from './Analytics.module.css';
import { MemberEngagementModal } from './MemberEngagementModal';

interface AnalyticsProps {
  circleId: string;
}

export const Analytics: React.FC<AnalyticsProps> = ({ circleId }) => {
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    dayjs().subtract(30, 'day').toDate(),
    dayjs().toDate(),
  ]);
  const [engagements, setEngagements] = useState<
    ChallengeEngagementPostMetrics[]
  >([]);
  const [notEngagedMembers, setNotEngagedMembers] = useState<
    NotEngagedMemberMetrics[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotEngagedLoading, setIsNotEngagedLoading] = useState(true);
  const [sortBy, setSortBy] = useState<EngagementSortBy>(EngagementSortBy.DATE);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);

  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPostTitle, setSelectedPostTitle] = useState<string>('');
  const [modalType, setModalType] = useState<
    'completed' | 'comments' | 'not-engaged'
  >('completed');
  const [postViewers, setPostViewers] = useState<PostMemberEngagementMetrics[]>(
    []
  );
  const [postNotEngagedMembers, setPostNotEngagedMembers] = useState<
    NotEngagedMemberMetrics[]
  >([]);
  const [modalLoading, setModalLoading] = useState(false);

  const [tribeMemberEngagements, setTribeMemberEngagements] = useState<
    PostMemberEngagementMetrics[]
  >([]);
  const [isTribeMembersLoading, setIsTribeMembersLoading] = useState(true);

  const fetchEngagements = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await engagementsApi.getTribePostsEngagements({
        tribeId: circleId,
        page: 1,
        pageSize: 50,
        from: dateRange[0],
        to: dateRange[1],
        sortBy,
        sortOrder,
      });
      setEngagements(result);
    } catch (error) {
      console.error('Failed to fetch engagements:', error);
    } finally {
      setIsLoading(false);
    }
  }, [circleId, dateRange, sortBy, sortOrder]);

  const fetchNotEngagedMembers = useCallback(async () => {
    try {
      setIsNotEngagedLoading(true);
      const result = await engagementsApi.getNotEngagedMembers({
        tribeId: circleId,
        page: 1,
        pageSize: 100,
        from: dateRange[0],
        to: dateRange[1],
        minEngagementThreshold: 0,
      });
      setNotEngagedMembers(result);
    } catch (error) {
      console.error('Failed to fetch not-engaged members:', error);
    } finally {
      setIsNotEngagedLoading(false);
    }
  }, [circleId, dateRange]);

  const fetchPostViewers = useCallback(async (postId: string) => {
    try {
      setModalLoading(true);
      const result = await engagementsApi.getPostViewers({
        postId,
        page: 1,
      });
      setPostViewers(result);
    } catch (error) {
      console.error('Failed to fetch post viewers:', error);
    } finally {
      setModalLoading(false);
    }
  }, []);

  const fetchPostNotEngagedMembers = useCallback(
    async (postId: string) => {
      try {
        setModalLoading(true);
        const result = await engagementsApi.getPostNotEngagedMembers({
          postId,
          page: 1,
          pageSize: 150,
          from: dateRange[0],
          to: dateRange[1],
          minEngagementThreshold: 0,
        });
        setPostNotEngagedMembers(result);
      } catch (error) {
        console.error('Failed to fetch post not engaged members:', error);
      } finally {
        setModalLoading(false);
      }
    },
    [dateRange]
  );

  const fetchTribeMemberEngagements = useCallback(async () => {
    try {
      setIsTribeMembersLoading(true);
      const result = await engagementsApi.getTribeMemberEngagements({
        tribeId: circleId,
        page: 1,
        pageSize: 100,
        from: dateRange[0],
        to: dateRange[1],
      });
      setTribeMemberEngagements(result);
    } catch (error) {
      console.error('Failed to fetch Circle member engagements:', error);
    } finally {
      setIsTribeMembersLoading(false);
    }
  }, [circleId, dateRange]);

  useEffect(() => {
    fetchEngagements();
    fetchNotEngagedMembers();
    fetchTribeMemberEngagements();
  }, [fetchEngagements, fetchNotEngagedMembers, fetchTribeMemberEngagements]);

  const handleDateChange: RangePickerProps['onChange'] = (
    dates,
    dateStrings
  ) => {
    if (dates) {
      setDateRange([dates[0]!.toDate(), dates[1]!.toDate()]);
    }
  };

  const handleSort = (sortField: EngagementSortBy) => {
    if (sortBy === sortField) {
      setSortOrder(
        sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC
      );
    } else {
      setSortBy(sortField);
      setSortOrder(SortOrder.DESC);
    }
  };

  const handleCellClick = (
    postId: string,
    postTitle: string,
    type: 'completed' | 'comments' | 'not-engaged'
  ) => {
    setSelectedPostTitle(postTitle);
    setModalType(type);
    setIsModalVisible(true);

    if (type === 'not-engaged') {
      fetchPostNotEngagedMembers(postId);
    } else {
      fetchPostViewers(postId);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedPostTitle('');
    setPostViewers([]);
    setPostNotEngagedMembers([]);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <DatePicker.RangePicker
          value={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
          onChange={handleDateChange}
          className={styles.datePicker}
        />
        <button
          className={styles.lastThirtyDays}
          onClick={() =>
            setDateRange([
              dayjs().subtract(30, 'day').toDate(),
              dayjs().toDate(),
            ])
          }
        >
          Show last 30 days
        </button>
      </div>

      <div className={styles.challengesTable}>
        <h2>Challenges</h2>
        <table>
          <thead>
            <tr>
              <th
                onClick={() => handleSort(EngagementSortBy.DATE)}
                className={styles.sortHeader}
              >
                <span className={styles.sortLabelRow}>
                  Date
                  <span className={styles.sortIcons}>
                    <CaretUpOutlined
                      className={
                        sortBy === EngagementSortBy.DATE &&
                        sortOrder === SortOrder.ASC
                          ? styles.sortIconActive
                          : styles.sortIconInactive
                      }
                    />
                    <CaretDownOutlined
                      className={
                        sortBy === EngagementSortBy.DATE &&
                        sortOrder === SortOrder.DESC
                          ? styles.sortIconActive
                          : styles.sortIconInactive
                      }
                    />
                  </span>
                </span>
              </th>
              <th className={styles.sortHeader} style={{ cursor: 'default' }}>
                Action
              </th>
              <th
                onClick={() => handleSort(EngagementSortBy.VIEWS)}
                className={styles.sortHeader}
              >
                <span className={styles.sortLabelRow}>
                  Members Viewed
                  <span className={styles.sortIcons}>
                    <CaretUpOutlined
                      className={
                        sortBy === EngagementSortBy.VIEWS &&
                        sortOrder === SortOrder.ASC
                          ? styles.sortIconActive
                          : styles.sortIconInactive
                      }
                    />
                    <CaretDownOutlined
                      className={
                        sortBy === EngagementSortBy.VIEWS &&
                        sortOrder === SortOrder.DESC
                          ? styles.sortIconActive
                          : styles.sortIconInactive
                      }
                    />
                  </span>
                </span>
              </th>
              <th
                onClick={() => handleSort(EngagementSortBy.VIEWS)}
                className={styles.sortHeader}
              >
                <span className={styles.sortLabelRow}>
                  Total Views
                  <span className={styles.sortIcons}>
                    <CaretUpOutlined
                      className={
                        sortBy === EngagementSortBy.VIEWS &&
                        sortOrder === SortOrder.ASC
                          ? styles.sortIconActive
                          : styles.sortIconInactive
                      }
                    />
                    <CaretDownOutlined
                      className={
                        sortBy === EngagementSortBy.VIEWS &&
                        sortOrder === SortOrder.DESC
                          ? styles.sortIconActive
                          : styles.sortIconInactive
                      }
                    />
                  </span>
                </span>
              </th>
              <th
                onClick={() => handleSort(EngagementSortBy.COMMENTS)}
                className={styles.sortHeader}
              >
                <span className={styles.sortLabelRow}>
                  Comments
                  <span className={styles.sortIcons}>
                    <CaretUpOutlined
                      className={
                        sortBy === EngagementSortBy.COMMENTS &&
                        sortOrder === SortOrder.ASC
                          ? styles.sortIconActive
                          : styles.sortIconInactive
                      }
                    />
                    <CaretDownOutlined
                      className={
                        sortBy === EngagementSortBy.COMMENTS &&
                        sortOrder === SortOrder.DESC
                          ? styles.sortIconActive
                          : styles.sortIconInactive
                      }
                    />
                  </span>
                </span>
              </th>
              <th className={styles.sortHeader} style={{ cursor: 'default' }}>
                Forum Views
              </th>
              <th className={styles.sortHeader} style={{ cursor: 'default' }}>
                Not Engaged
              </th>
            </tr>
          </thead>
          <tbody>
            {engagements.map((engagement: ChallengeEngagementPostMetrics) => (
              <tr key={engagement.post._id}>
                <td>{dayjs(engagement.post.createdAt).format('MM/DD/YYYY')}</td>
                <td className={styles.challengeCell}>
                  <Link
                    to={`/circle/${engagement.post.tribe_id}/challenge/${engagement.post._id}`}
                    className={styles.challengeLink}
                  >
                    {engagement.post.challenges[0]?.name ?? 'POST'}
                  </Link>
                </td>
                <td
                  className={`${styles.membersViewedCount} ${styles.clickable}`}
                  onClick={() =>
                    handleCellClick(
                      engagement.post._id,
                      engagement.post.challenges[0]?.name ?? 'POST',
                      'completed'
                    )
                  }
                >
                  {engagement.engagement.completed}
                </td>
                <td>{engagement.engagement.totalCompletedViews}</td>
                <td>{engagement.engagement.comments}</td>
                <td>{engagement.engagement.forumViews}</td>
                <td
                  className={`${styles.notEngagedCount} ${styles.clickable}`}
                  onClick={() =>
                    handleCellClick(
                      engagement.post._id,
                      engagement.post.challenges[0]?.name ?? 'POST',
                      'not-engaged'
                    )
                  }
                >
                  {postNotEngagedMembers?.length ?? 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.notEngagedTable}>
        <h2>Not Engaged Members</h2>
        {isNotEngagedLoading ? (
          <div className={styles.loadingRow}>
            Loading not engaged members...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className={styles.sortHeader} style={{ cursor: 'default' }}>
                  <span className={styles.sortLabelRow}>User</span>
                </th>
                <th className={styles.sortHeader} style={{ cursor: 'default' }}>
                  <span className={styles.sortLabelRow}>Last Activity</span>
                </th>
                <th className={styles.sortHeader} style={{ cursor: 'default' }}>
                  <span className={styles.sortLabelRow}>Days Inactive</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {notEngagedMembers.length === 0 ? (
                <tr>
                  <td colSpan={3} className={styles.emptyRow}>
                    All members are engaged! 🎉
                  </td>
                </tr>
              ) : (
                notEngagedMembers.map((member: NotEngagedMemberMetrics) => (
                  <tr key={member.member._id}>
                    <td>
                      <div className={styles.memberCell}>
                        <img
                          src={member.member.user.avatar_url}
                          alt={member.member.user.full_name}
                          className={styles.avatar}
                        />
                        {member.member.user.full_name}
                      </div>
                    </td>
                    <td>
                      {member.lastActivity ? (
                        dayjs(member.lastActivity).format('MM/DD/YYYY')
                      ) : (
                        <span className={styles.neverText}>Never</span>
                      )}
                    </td>
                    <td>
                      {member.daysSinceLastActivity !== undefined ? (
                        `${member.daysSinceLastActivity} days`
                      ) : (
                        <span className={styles.naText}>N/A</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.challengesTable}>
        <h2>Circle Member Engagement</h2>
        {isTribeMembersLoading ? (
          <div className={styles.loadingRow}>
            Loading Circle member engagement...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className={styles.sortHeader} style={{ cursor: 'default' }}>
                  <span className={styles.sortLabelRow}>Member</span>
                </th>
                <th className={styles.sortHeader} style={{ cursor: 'default' }}>
                  <span className={styles.sortLabelRow}>Completed Views</span>
                </th>
                <th className={styles.sortHeader} style={{ cursor: 'default' }}>
                  <span className={styles.sortLabelRow}>Comments</span>
                </th>
                <th className={styles.sortHeader} style={{ cursor: 'default' }}>
                  <span className={styles.sortLabelRow}>Forum Views</span>
                </th>
                <th className={styles.sortHeader} style={{ cursor: 'default' }}>
                  <span className={styles.sortLabelRow}>Member Since</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {tribeMemberEngagements.length === 0 ? (
                <tr>
                  <td colSpan={5} className={styles.emptyRow}>
                    No Circle members found.
                  </td>
                </tr>
              ) : (
                tribeMemberEngagements.map((member) => (
                  <tr key={member.member._id}>
                    <td className={styles.challengeCell}>
                      <div className={styles.memberCell}>
                        <img
                          src={member.member.user.avatar_url}
                          alt={member.member.user.full_name}
                          className={styles.avatar}
                        />
                        {member.member.user.full_name}
                      </div>
                    </td>
                    <td>{member.engagement.completed}</td>
                    <td>{member.engagement.comments}</td>
                    <td>{member.engagement.forumViews}</td>
                    <td>
                      {(member.member as any).joinedAt
                        ? dayjs((member.member as any).joinedAt).format(
                            'MM/DD/YYYY'
                          )
                        : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <MemberEngagementModal
        isVisible={isModalVisible}
        postTitle={selectedPostTitle}
        modalType={modalType}
        postViewers={postViewers}
        postNotEngagedMembers={postNotEngagedMembers}
        loading={modalLoading}
        onClose={handleModalClose}
      />
    </div>
  );
};
